/**
 * Catálogo completo de códigos de alarma — fuente de la "chuleta de
 * alarmas" (panel de consulta). Más amplio que `AlarmCodes`
 * (detailedSimulation.ts): incluye códigos de confianza media/baja y
 * códigos oficiales sin significado documentado en ninguna fuente
 * disponible, marcados como tal en vez de inventar una descripción.
 *
 * Investigado 2026-09-11 contra la spec oficial del Protocolo B
 * (`protocolo-b/3-especificaciones-generales/3.2-especificacion-del-protocolo.md`
 * §3.2.2.4.3) y los comentarios en español del simulador `repos/regulador/`.
 */
import { AlarmCodes } from '../store/detailedSimulation';
import { customAlarmCodes } from './overridesRuntime';

export type AlarmConfidence = 'alta' | 'media' | 'baja' | 'sin-documentar';

export interface AlarmCatalogEntry {
  code: string;
  description: string;
  confidence: AlarmConfidence;
  source: string;
}

export const ALARM_CATALOG: AlarmCatalogEntry[] = [
  ...Object.entries(AlarmCodes).map(([code, description]) => ({
    code,
    description,
    confidence: 'alta' as const,
    source: 'Protocolo B §3.2.2.4.3',
  })),

  // Confianza media — subalarmas o inferidos por contexto de código/spec.
  { code: 'Cs', description: 'Subalarma de incompatibilidad de color — indica la salida/color concreto', confidence: 'media', source: 'Protocolo B §3.2.2.4.3 (dato: estado de la salida)' },
  { code: 'Dd', description: 'Subalarma de demanda — indica el número de demanda concreto', confidence: 'media', source: 'Protocolo B §3.2.2.4.3 (dato: número de demanda)' },
  { code: 'Gs', description: 'Subalarma de sustitución de grupo — indica la salida que sustituye', confidence: 'media', source: 'Protocolo B §3.2.2.4.3 (dato: salida que sustituye)' },
  { code: 'AE<n>', description: 'Alarma externa numerada (patrón AE1, AE2…) — variante numerada de "X"', confidence: 'media', source: 'regulador/core/state_machine.py:599-603' },

  // Confianza baja — solo listados en la tabla de spec, sin prosa descriptiva.
  { code: 'IFF', description: 'Corriente de fuga/fallo detectada (nombre exacto sin confirmar)', confidence: 'baja', source: 'Protocolo B §3.2.2.4.3 (dato: corriente medida)' },
  { code: 'IFA', description: 'Corriente de fuga/fallo, variante A (nombre exacto sin confirmar)', confidence: 'baja', source: 'Protocolo B §3.2.2.4.3 (dato: corriente medida)' },
  { code: 'SAIB', description: 'Alimentación ininterrumpida (SAI) — % de carga de batería (sigla sin confirmar)', confidence: 'baja', source: 'Protocolo B §3.2.2.4.3 (dato: % de carga)' },
  { code: 'KT', description: 'Manipulación/tecleo local (cantidad de órdenes introducidas)', confidence: 'baja', source: 'Protocolo B §3.2.2.4.3 (dato: cantidad de órdenes)' },
  { code: 'EA', description: 'Error de tarea/proceso interno (nombre de tarea + código de error)', confidence: 'baja', source: 'Protocolo B §3.2.2.4.3 (dato: nombre de tarea + código de error)' },

  // Sin ninguna pista de significado en ninguna fuente disponible.
  { code: 'KH', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Solo mencionado como ejemplo genérico, sin descripción' },
  { code: 'EE', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "permanente")' },
  { code: 'ET', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "permanente")' },
  { code: 'DFF', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "transitoria")' },
  { code: 'MDA', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "transitoria")' },
  { code: 'RMT', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "transitoria")' },
  { code: 'VTO', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "permanente")' },
  { code: 'TB', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "permanente")' },
  { code: 'Q', description: 'Código oficial del Protocolo B, sin significado documentado', confidence: 'sin-documentar', source: 'Protocolo B §3.2.2.4.3 (solo tipo "transitoria")' },

  // Código propio de ARS93, fuera de la tabla oficial del Protocolo B.
  {
    code: 'CD',
    description: 'Cambio de horario',
    confidence: 'alta',
    source: 'Código propio de ARS93',
  },
];

// Códigos del catálogo con significado confirmado que no forman parte del
// vocabulario base del Protocolo B (`AlarmCodes`) — se resuelven igual que
// los oficiales en panel, toast y narración por voz.
const CATALOG_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
  ALARM_CATALOG.filter(e => e.confidence === 'alta').map(e => [e.code, e.description]),
);

/** Traduce un código de alarma a texto legible: primero busca en las
 * alarmas propias de este municipio (SC-43, guardado directo vía Modo
 * Edición), luego en el catálogo estándar; si no está en ninguno, devuelve
 * el código tal cual — nunca se inventa una descripción. */
export function resolveAlarmDescription(code: string): string {
  const custom = customAlarmCodes.find(c => c.code === code);
  if (custom) return custom.description;
  return AlarmCodes[code as keyof typeof AlarmCodes] || CATALOG_DESCRIPTIONS[code] || code;
}
